/**
 * Portal slug generation utilities
 * Handles slug creation, validation, and collision detection for portal URLs
 */

// Reserved words that cannot be used as portal slugs
const RESERVED_WORDS = [
  'admin',
  'api',
  'portal',
  'settings',
  'app',
  'dashboard',
  'login',
  'signup',
  'auth',
  'sign-in',
  'sign-up',
  'start-trial',
  'get-started',
  'workspaces',
  'deals',
  'reports',
  'team',
  'help',
  'support',
  'pricing',
  'about',
  'contact',
  'terms',
  'privacy',
  'sso-callback',
];

/**
 * Convert text to URL-friendly slug
 * - Lowercase
 * - Replace spaces with hyphens
 * - Remove special characters
 * - Limit to 50 characters
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
    .substring(0, 50);
}

/**
 * Validate a slug against rules
 * - 3-50 characters
 * - Lowercase alphanumeric and hyphens only
 * - Not in reserved words list
 */
export function validateSlug(slug: string): { valid: boolean; error?: string } {
  if (!slug) {
    return { valid: false, error: 'Slug is required' };
  }

  if (slug.length < 3) {
    return { valid: false, error: 'Slug must be at least 3 characters' };
  }

  if (slug.length > 50) {
    return { valid: false, error: 'Slug must be 50 characters or less' };
  }

  if (!/^[a-z0-9-]+$/.test(slug)) {
    return { valid: false, error: 'Slug can only contain lowercase letters, numbers, and hyphens' };
  }

  if (slug.startsWith('-') || slug.endsWith('-')) {
    return { valid: false, error: 'Slug cannot start or end with a hyphen' };
  }

  if (RESERVED_WORDS.includes(slug)) {
    return { valid: false, error: 'This slug is reserved and cannot be used' };
  }

  return { valid: true };
}

/**
 * Generate a unique portal slug from workspace name
 * - Slugifies the name
 * - Checks against existing slugs for collisions
 * - Appends -2, -3, etc. if collision found
 * - Falls back to 'portal' if name is empty
 */
export function generatePortalSlug(
  name: string,
  existingSlugs: string[],
  existingSlug?: string
): string {
  // If there's already a slug, return it
  if (existingSlug) {
    return existingSlug;
  }

  // Create base slug from name
  let baseSlug = slugify(name);

  // Fallback if name produces empty slug
  if (!baseSlug) {
    baseSlug = 'portal';
  }

  // Check if base slug is valid and unique
  let slug = baseSlug;
  let counter = 2;

  // Keep appending numbers until we find a unique slug
  while (existingSlugs.includes(slug) || RESERVED_WORDS.includes(slug)) {
    slug = `${baseSlug}-${counter}`;
    counter++;
  }

  return slug;
}

/**
 * Check if a slug is a reserved word
 */
export function isReservedSlug(slug: string): boolean {
  return RESERVED_WORDS.includes(slug.toLowerCase());
}
